import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Brand.css";
import "../styles/global.css";

/* ────────── Scroll Lock Fix Helper ────────── */
const unlockBodyScroll = () => {
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
  document.documentElement.style.removeProperty("overflow");
  document.documentElement.style.removeProperty("padding-right");
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
};

/* ────────── Custom Pagination ────────── */
const ChevronLeftIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  const start = Math.min((currentPage - 1) * perPage + 1, totalRows);
  const end = Math.min(currentPage * perPage, totalRows);

  return (
    <div className="cat-pagination">
      <div className="cat-pagination__info">
        <span>
          Showing <b>{start}</b>–<b>{end}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="cat-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="cat-pagination__pages">
        <button
          className="cat-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="cat-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`cat-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="cat-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

/* ────────── iPhone Toggle Switch ────────── */
const IosSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`ios-switch ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
  >
    <span className="ios-switch__thumb" />
  </button>
);

/* ────────── Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const ROLE_OPTIONS = ["Admin", "Manager", "Staff", "User"];

/* Roles allowed to sign in to the admin dashboard. "User" exists as a
   role for record-keeping/filtering here, but is intentionally left
   out of this list — Login.js uses it to block dashboard access for
   that role (and any other role/blank role not listed here). */
export const ADMIN_LOGIN_ALLOWED_ROLES = ["Admin", "Manager", "Staff"];

/* ────────── Main Component ────────── */
const Users = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("Staff");
  const [status, setStatus] = useState("Active");
  const [password, setPassword] = useState("");

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const token = localStorage.getItem("authToken");

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleClearRows, setToggleClearRows] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", confirmLabel: "Confirm",
    confirmVariant: "danger", onConfirm: null,
  });
  const showConfirm = ({ title, message, confirmLabel, confirmVariant = "danger", onConfirm }) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmVariant, onConfirm });
  };
  const closeConfirm = () => setConfirmDialog((p) => ({ ...p, open: false, onConfirm: null }));

  /* ── Attach hidden.bs.modal listener ONCE to always unlock scroll ── */
  useEffect(() => {
    const modalEl = document.getElementById("addUserModal");
    if (!modalEl) return;
    const handleModalHidden = () => unlockBodyScroll();
    modalEl.addEventListener("hidden.bs.modal", handleModalHidden);
    return () => modalEl.removeEventListener("hidden.bs.modal", handleModalHidden);
  }, []);

  /* ── Fetch Users (server-side search/filter/pagination) ── */
  const fetchUsers = useCallback((page = 1, size = perPage, search = filterText, roleF = roleFilter, statusF = statusFilter) => {
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: size });
    if (search) params.append("search", search);
    if (roleF) params.append("role", roleF);
    if (statusF) params.append("status", statusF);

    fetch(`${API_BASE_URL}api/users?${params.toString()}`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setUsers(data.data);
          setTotalRows(data.pagination.total);
          setCurrentPage(data.pagination.current_page);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [perPage, filterText, roleFilter, statusFilter, token]);

  useEffect(() => { fetchUsers(1, perPage); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Debounced search + filter change → refetch page 1 ── */
  useEffect(() => {
    const t = setTimeout(() => { fetchUsers(1, perPage, filterText, roleFilter, statusFilter); }, 350);
    return () => clearTimeout(t);
  }, [filterText, roleFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page) => fetchUsers(page, perPage);
  const handlePerPageChange = (newPerPage) => { setPerPage(newPerPage); fetchUsers(1, newPerPage); };
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  /* ── Status toggle ── */
  const handleStatusChange = (id, currentStatus) => {
    if (switchingId === id) return;
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    showConfirm({
      title: "Change Status",
      message: `Are you sure you want to set this user to "${newStatus}"?`,
      confirmLabel: "Yes, Change",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setSwitchingId(id);
        fetch(`${API_BASE_URL}api/users/status/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              setMessageAlert(`Status updated to ${newStatus} successfully!`);
              setMessageType("success");
              fetchUsers(currentPage, perPage);
            } else {
              setMessageAlert("Failed to update status.");
              setMessageType("danger");
            }
          })
          .catch(() => { setMessageAlert("Error while updating status."); setMessageType("danger"); })
          .finally(() => {
            setSwitchingId(null);
            setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
          });
      },
    });
  };

  const closeModal = useCallback(() => {
    const modalEl = document.getElementById("addUserModal");
    if (!modalEl) return;
    const modalInstance = window.bootstrap?.Modal?.getInstance(modalEl);
    if (modalInstance) modalInstance.hide();
    else unlockBodyScroll();
  }, []);

  const resetForm = () => {
    setName(""); setEmail(""); setPhone(""); setRole("Staff"); setStatus("Active"); setPassword("");
    setIsEditMode(false); setEditId(null);
  };

  /* ── Save user (create / update) ── */
  const handleSaveUser = (e) => {
    e.preventDefault();

    const payload = { name, email, phone, role, status };
    if (password) payload.password = password; // only send if set (required on create, optional on edit)

    const url = isEditMode ? `${API_BASE_URL}api/users/${editId}` : `${API_BASE_URL}api/users`;

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          setMessageAlert(isEditMode ? "User updated successfully!" : "User added successfully!");
          setMessageType("success");
          resetForm();
          fetchUsers(currentPage, perPage);
          closeModal();
        } else {
          setMessageAlert(data.message || "Failed to save user.");
          setMessageType("danger");
        }
      })
      .catch(() => { setMessageAlert("Something went wrong!"); setMessageType("danger"); })
      .finally(() => { setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000); });
  };

  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditId(row.id);
    setName(row.name || "");
    setEmail(row.email || "");
    setPhone(row.phone || "");
    setRole(row.role || "Staff");
    setStatus(row.status || "Active");
    setPassword("");
    const modalEl = document.getElementById("addUserModal");
    new window.bootstrap.Modal(modalEl).show();
  };

  const handleAddNew = () => {
    resetForm();
    const modalEl = document.getElementById("addUserModal");
    new window.bootstrap.Modal(modalEl).show();
  };

  /* ── Single Delete ── */
  const handleDelete = (id) => {
    showConfirm({
      title: "Delete User",
      message: "Are you sure you want to delete this user? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/users/destroy/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              setMessageAlert("User deleted successfully!");
              setMessageType("success");
              fetchUsers(currentPage, perPage);
            } else {
              setMessageAlert(data.message || "Failed to delete user.");
              setMessageType("danger");
            }
          })
          .catch(() => { setMessageAlert("Error deleting user."); setMessageType("danger"); })
          .finally(() => { setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000); });
      },
    });
  };

  /* ── Bulk Delete ── */
  const handleSelectedRowsChange = ({ selectedRows: rows }) => setSelectedRows(rows);

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected user${count === 1 ? "" : "s"}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count === 1 ? "" : "s"}`,
      confirmVariant: "danger",
      onConfirm: async () => {
        closeConfirm();
        setBulkDeleting(true);
        try {
          const results = await Promise.all(
            selectedRows.map((row) =>
              fetch(`${API_BASE_URL}api/users/destroy/${row.id}`, {
                method: "GET",
                headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
              }).then((res) => res.json()).catch(() => ({ status: false }))
            )
          );
          const successCount = results.filter((r) => r.status).length;
          const failCount = count - successCount;
          if (failCount === 0) {
            setMessageAlert(`${successCount} user${successCount === 1 ? "" : "s"} deleted successfully!`);
            setMessageType("success");
          } else {
            setMessageAlert(`${successCount} deleted, ${failCount} failed.`);
            setMessageType(successCount > 0 ? "warning" : "danger");
          }
          setSelectedRows([]);
          setToggleClearRows((p) => !p);
          fetchUsers(currentPage, perPage);
        } catch {
          setMessageAlert("Error during bulk delete.");
          setMessageType("danger");
        } finally {
          setBulkDeleting(false);
          setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
        }
      },
    });
  };

  const customStyles = useMemo(() => ({
    table: { style: { background: "transparent" } },
    headRow: { style: { background: "#f0fbf6", borderTopLeftRadius: "0px", borderTopRightRadius: "0px", borderBottom: "1px solid #d6efe2", minHeight: "52px" } },
    headCells: { style: { fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#5b6b66", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
      style: { minHeight: "72px", fontSize: "14px", color: "#1f2a37", borderBottom: "1px solid #eef4f1 !important", transition: "background 0.18s ease", fontFamily: "'Inter', sans-serif" },
      highlightOnHoverStyle: { background: "#f6fcf9", borderBottomColor: "#e3f0e9", outline: "none" },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  }), []);

  const columns = useMemo(() => [
    {
      name: "ID", selector: (row) => row.id, sortable: true, width: "70px",
      cell: (row) => <span className="br-id-pill">#{row.id}</span>,
    },
    {
      name: "Name", selector: (row) => row.name, sortable: true, width: "160px",
      cell: (row) => <span className="br-name-cell" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }} title={row.name}>{row.name}</span>,
    },
    {
      name: "Email", selector: (row) => row.email, width: "220px",
      cell: (row) => <span className="br-slug-cell" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", maxWidth: "100%" }} title={row.email}>{row.email}</span>,
    },
    {
      name: "Phone", selector: (row) => row.phone, width: "120px",
      cell: (row) => <span className="br-slug-cell">{row.phone || "—"}</span>,
    },
    {
      name: "Role", selector: (row) => row.role, sortable: true, width: "110px",
      cell: (row) => <span className="br-slug-cell">{row.role || "—"}</span>,
    },
    {
      name: "Status", width: "140px",
      cell: (row) => (
        <div className="br-status-wrap">
          <IosSwitch
            checked={row.status === "Active"}
            onChange={() => handleStatusChange(row.id, row.status)}
            disabled={switchingId === row.id}
          />
          <span className={`br-status-label ${row.status === "Active" ? "is-active" : "is-inactive"}`}>
            {row.status === "Active" ? "Active" : "Inactive"}
          </span>
        </div>
      ),
    },
    {
      name: "Created At", selector: (row) => row.created_at, sortable: true, width: "130px",
      cell: (row) => <span className="br-date-cell">{row.created_at ? new Date(row.created_at).toLocaleDateString("en-IN") : "—"}</span>,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="br-actions">
          <button
            className="br-action-btn br-edit"
            onClick={() => handleEdit(row)}
            title="Edit User"
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button
            className="br-action-btn br-del"
            onClick={() => handleDelete(row.id)}
            title="Delete User"
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      ),
      width: "110px",
      right: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ], [switchingId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        {(loading || bulkDeleting) && (
          <div className="br-overlay">
            <div className="br-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="brand-shell">

              <div className="brand-head">
                <div>
                  <h2>User Management</h2>
                  <p>Manage admin panel users — add, edit, or deactivate accounts.</p>
                </div>
                <button type="button" className="add-btn" onClick={handleAddNew}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add User
                </button>
              </div>

              <div className="brand-card">
                <div className="card-top" style={{ flexWrap: "wrap", gap: 12 }}>
                  <div className="search-box">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search name / email / phone…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <select className="form-select" style={{ maxWidth: 170 }} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                    <option value="">All Roles</option>
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select className="form-select" style={{ maxWidth: 170 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="Active">Active</option>
                    <option value="InActive">Inactive</option>
                  </select>
                  <div className="total-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" />
                    </svg>
                    Total:&nbsp;<b>{totalRows}</b>
                  </div>
                </div>

                {messagealert && (
                  <div className={`br-alert ${messageType}`} role="alert">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {messagealert}
                    <button type="button" className="close-btn" onClick={() => setMessageAlert("")}>×</button>
                  </div>
                )}

                {selectedRows.length > 0 && (
                  <div className="bulk-bar">
                    <div className="bulk-info">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                        <polyline points="9 11 12 14 22 4" />
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                      </svg>
                      <strong>{selectedRows.length}</strong>&nbsp;item{selectedRows.length === 1 ? "" : "s"} selected
                    </div>
                    <div className="bulk-actions">
                      <button type="button" className="bulk-clear" onClick={() => { setSelectedRows([]); setToggleClearRows(!toggleClearRows); }}>Clear</button>
                      <button type="button" className="bulk-delete-btn" onClick={handleBulkDelete} disabled={bulkDeleting}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                        </svg>
                        {bulkDeleting ? "Deleting…" : "Delete Selected"}
                      </button>
                    </div>
                  </div>
                )}

                <DataTable
                  columns={columns}
                  data={users}
                  customStyles={customStyles}
                  highlightOnHover
                  striped
                  responsive
                  selectableRows
                  onSelectedRowsChange={handleSelectedRowsChange}
                  clearSelectedRows={toggleClearRows}
                  pagination={false}
                  progressPending={loading}
                  noDataComponent={
                    <div style={{ padding: "56px 24px", textAlign: "center", color: "var(--br-muted)" }}>
                      <p style={{ fontWeight: 600, fontSize: 15, color: "#334155", margin: "0 0 4px" }}>No users found</p>
                      <p style={{ fontSize: 13.5, margin: 0 }}>Click "Add User" to create one, or adjust your filters.</p>
                    </div>
                  }
                />

                {totalRows > 0 && (
                  <CustomPagination
                    currentPage={currentPage} totalPages={totalPages}
                    onPageChange={handlePageChange} perPage={perPage}
                    totalRows={totalRows} onPerPageChange={handlePerPageChange}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Add / Edit Modal */}
          <div className="modal fade" id="addUserModal" tabIndex="-1" aria-labelledby="addUserModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <form id="addUserForm" onSubmit={handleSaveUser}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="addUserModalLabel">{isEditMode ? "Edit User" : "Add New User"}</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                  </div>
                  <div className="modal-body">
                    <div className="row g-4">
                      <div className="col-12">
                        <label className="form-label">Name</label>
                        <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Email</label>
                        <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} required />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Phone</label>
                        <input type="text" className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Role</label>
                        <select className="form-select" value={role} onChange={(e) => setRole(e.target.value)}>
                          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label">Status</label>
                        <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                          <option value="Active">Active</option>
                          <option value="InActive">Inactive</option>
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label">
                          Password {isEditMode && <small className="text-muted">(leave blank to keep unchanged)</small>}
                        </label>
                        <input
                          type="password" className="form-control" value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required={!isEditMode}
                          minLength={6}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                    <button type="submit" className="btn btn-primary">{isEditMode ? "Update User" : "Add User"}</button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Users;